import { Injectable } from '@angular/core';
import { HttpRequest, HttpInterceptor, HttpEvent, HttpResponse, HttpHandler } from '@angular/common/http';
import { Router } from '@angular/router';
import { from, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AppService, DataService, LocalStorageService } from '../service';
import { SigningKeyService } from './signing-key.service';
import { RequestSigningService } from './request-signing.service';

const BACKEND_PATH_FRAGMENT = '/facts_backend-2.6/rest/';

const SIGNATURE_EXCLUDED_URL_FRAGMENTS = [
  '/permissions/login',
  '/permissions/externalLogin',
  '/permissions/adLogin',
  '/permissions/sso_login',
  '/permissions/simple/login',
  '/permissions/Entra/login',
  '/contractorPermissions/login',
  '/saml/',
  '/oauth/client/getToken',
  '/file/',
  '/security/client-keys'
];

@Injectable()
export class JWTInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private translateService: TranslateService,
    public appService: AppService,
    public dataService: DataService,
    private localStorageService: LocalStorageService,
    private signingKey: SigningKeyService,
    private requestSigning: RequestSigningService,
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.shouldSign(req)) {
      return next.handle(req).pipe(this.responseTap());
    }
    return from(this.signAndAttach(req)).pipe(
      switchMap(signedReq => next.handle(signedReq).pipe(this.responseTap()))
    );
  }

  private shouldSign(req: HttpRequest<any>): boolean {
    const url = req.url || '';
    if (url.indexOf(BACKEND_PATH_FRAGMENT) === -1) {
      return false;
    }
    for (let i = 0; i < SIGNATURE_EXCLUDED_URL_FRAGMENTS.length; i++) {
      if (url.indexOf(SIGNATURE_EXCLUDED_URL_FRAGMENTS[i]) !== -1) {
        return false;
      }
    }
    return true;
  }

  private async signAndAttach(req: HttpRequest<any>): Promise<HttpRequest<any>> {
    await this.signingKey.ready$;
    const token = this.localStorageService.getStore('token') || '';
    const sigHeaders = await this.requestSigning.sign(req, token);
    if (!sigHeaders) {
      return req;
    }
    return req.clone({ setHeaders: sigHeaders as any });
  }

  private responseTap() {
    return tap(
      (event: any) => {
        if (event instanceof HttpResponse) {
          if (event.body && event.body.STATUS === -2 && event.body.MSG === 'SQL ERROR') {
            this.errorHandler({
              status: event.body.STATUS,
              message: event.body.MSG
            });
          }
        }
      },
      (err: any) => {
        if (err && err.name === 'TimeoutError') {
          err.status = 0;
        } else {
          this.errorHandler(err);
        }
        return err;
      }
    );
  }

  private errorHandler(error) {
    let msg: string;
    switch (error.status) {
      case 401:
        if (!this.handleSignatureError(error)) {
          msg = this.translateService.instant('HTTP_ERROR_MSG.401');
          if (!this.isAtLoginPage()) {
            sessionStorage.setItem('savedUrl', this.router.url);
          }
          this.clearAuthState();
        }
        break;
      case 404:
        msg = this.translateService.instant('HTTP_ERROR_MSG.404');
        if (!this.isAtLoginPage()) {
          sessionStorage.setItem('savedUrl', this.router.url);
        }
        this.clearAuthState();
        break;
      case 413:
        msg = this.translateService.instant('HTTP_ERROR_MSG.413');
        break;
      case -2:
        msg = this.translateService.instant('HTTP_ERROR_MSG.SQL_ERROR');
        break;
      default:
        msg = error.message;
        break;
    }
    if (msg) {
      this.appService.toastTip(msg, true);
    }
  }

  private handleSignatureError(error: any): boolean {
    const code = error && error.error && error.error.MSG;
    if (typeof code !== 'string') { return false; }
    switch (code) {
      case 'CLIENT_KEY_MISSING':
      case 'CLIENT_KEY_INVALID':
      case 'CLIENT_KEY_REVOKED':
      case 'CLIENT_KEY_EXPIRED':
      case 'SIGNATURE_MISSING':
      case 'SIGNATURE_INVALID':
      case 'SIGNATURE_ALGORITHM_UNSUPPORTED':
      case 'BODY_HASH_MISMATCH':
      case 'TIMESTAMP_EXPIRED':
      case 'NONCE_REPLAYED':
      case 'NONCE_STORE_UNAVAILABLE':
        return true;
      default:
        return false;
    }
  }

  private isAtLoginPage(): boolean {
    return this.router.url === '/login' || this.router.url === '/contractor-login';
  }

  private clearAuthState() {
    this.appService.logout();
  }
}
